/**
 * Formats a template (its derived features and inputs) into the request shape the backend
 * expects for save — `formatTemplateInput` is the centerpiece, with `buildFormattedTemplate`,
 * `toFormattedDerivedFeature`, `toFormulaCreatorRule`, and `formatDerivedFeaturesUsingFormulas`
 * doing the actual shape conversion. That's the "formatting" the file is named for.
 *
 * The backend rejects a request whose `derivedFeatureIndex`/`inputFeatureIndex` values don't
 * match array position, or that still contains orphaned features, so formatting can't happen
 * without first sorting, reindexing, and pruning the working set — hence
 * `sortNewDerivedFeaturesByDependencies`, `reindexNewDerivedFeatures`/`reindexExistingDerivedFeatures`,
 * and `filterUnusedFeatures` living here too: they're required pre-steps of formatting, not a
 * separate concern. A handful of small pure identity/type-guard helpers (`getDerivedFeatureId`,
 * `featureNameToIdentifier`, `isFeatureTypeDerivedFeatureWithColumnDefinition`, etc.) round out
 * the set, needed by the above but not worth their own file.
 */

import { renameFormulaVariables } from '@/utilities/formulaVariables.utilities';
import { OutputTypeEnum } from '@seeqdev/api/model/InputFeatureV1';
import { TypeEnum as TemplateRuleInputV1ListStringTypeEnum } from '@seeqdev/api/model/TemplateRuleInputV1ListString';
import { TypeEnum as TemplateRuleInputV1ObjectTypeEnum } from '@seeqdev/api/model/TemplateRuleInputV1Object';
import { TypeEnum as TemplateRuleInputV1StringTypeEnum } from '@seeqdev/api/model/TemplateRuleInputV1String';
import { SeeqNames } from '@seeqdev/constants';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import type {
  DerivedFeatureWithColumnDefinition,
  FormulaOutput,
  IndexedFeature,
  NewlyAddedDerivedFeature,
  NewlyAddedInputFeature,
  TemplateDerivedFeature,
  TemplateInput,
  TemplateOutput,
  TemplateRuleInputWithFeatureId,
} from '@/tools/models/template.types';
import type { DerivedFeatureV1 } from '@seeqdev/api/model/DerivedFeatureV1';
import type { FeatureRuleV1 } from '@seeqdev/api/model/FeatureRuleV1';
import type { TemplateFormulaCreatorRuleV1 } from '@seeqdev/api/model/TemplateFormulaCreatorRuleV1';
import type { TemplateInputV1 } from '@seeqdev/api/model/TemplateInputV1';
import type { TemplateRuleInputV1ListString } from '@seeqdev/api/model/TemplateRuleInputV1ListString';
import type { TemplateRuleInputV1Object } from '@seeqdev/api/model/TemplateRuleInputV1Object';
import type { TemplateRuleInputV1String } from '@seeqdev/api/model/TemplateRuleInputV1String';

/**
 * Sorts derived features based on their dependencies. Features that are depended upon come before features that
 * depend on them. This prevents out-of-bounds errors in the backend. The indices of the derived features are
 * updated in the rules' inputs to reflect the new order.
 * @param derivedFeatures The derived features to sort
 */
export function sortNewDerivedFeaturesByDependencies(
  derivedFeatures: NewlyAddedDerivedFeature[],
): NewlyAddedDerivedFeature[] {
  const visitedIndices = new Set<number>();
  const sorted: IndexedFeature[] = [];

  const visitFeature = (index: number): void => {
    // Cycle detection is not needed here - these features come from items where cycles are
    // prohibited, so we only track visited indices to avoid redundant traversal
    if (visitedIndices.has(index)) return;
    visitedIndices.add(index);

    const feature = derivedFeatures[index];

    feature.rules!.forEach((rule) => {
      rule.inputs.forEach((input) => {
        if (input.derivedFeatureIndex !== undefined) {
          visitFeature(input.derivedFeatureIndex);
        }
      });
    });

    sorted.push({ feature, oldIndex: index });
  };

  derivedFeatures.forEach((_, index) => visitFeature(index));

  // Map each old index to its new index in sorted array
  const oldToNewIndex = new Map(sorted.map(({ oldIndex }, newIndex) => [oldIndex, newIndex]));

  // Rewrite derivedFeatureIndex in every rule input
  return sorted.map(({ feature }) => ({
    ...feature,
    rules: feature.rules!.map((rule) => ({
      ...rule,
      inputs: rule.inputs.map((input) => {
        if (input.derivedFeatureIndex === undefined) return input; // Dependent on an input, not a derived feature
        const newIndex = oldToNewIndex.get(input.derivedFeatureIndex);
        return newIndex !== undefined ? { ...input, derivedFeatureIndex: newIndex } : input;
      }),
    })),
  }));
}

/** Type guard to check if a rule input has the featureId field. */
export function hasFeatureId(input: TemplateRuleInputV1Object): input is TemplateRuleInputWithFeatureId {
  return 'featureId' in input;
}

/** Gets the ID of a feature from a rule input. Prefer inputFeatureId (template feature ID) over featureId (Seeq item
 *  ID). */
export const getFeatureIdFromInput = (input: TemplateRuleInputV1Object): string | undefined =>
  input.inputFeatureId ?? (hasFeatureId(input) ? input.featureId : undefined);

/** Gets the identifying id of a derived feature: `referenceId` for a newly added feature (not yet
 * persisted), otherwise its persisted `id`. */
export const getDerivedFeatureId = (feature: DerivedFeatureV1 | NewlyAddedDerivedFeature): string | undefined =>
  'referenceId' in feature ? feature.referenceId : feature.id;

/** Enriches all rule inputs with featureId tracking field by resolving current array indices.
 * Used internally by filterUnusedFeatures (for dependency tracking) and reindexNewDerivedFeatures
 * (for reliable index lookups). Preserves object identity when no enrichment is needed. */
export function enrichNewDerivedFeaturesWithFeatureId(
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  newInputFeatures: NewlyAddedInputFeature[],
): NewlyAddedDerivedFeature[] {
  return newDerivedFeatures.map((feature) => {
    if (!feature.rules?.length) return feature;

    // Track if anything changed so we only rebuild objects when necessary.
    // Preserves identity after initial enrichment to avoid wasted allocations.
    let anyRuleChanged = false;
    const enrichedRules = feature.rules.map((rule) => {
      if (!rule.inputs) return rule;

      let anyInputChanged = false;
      const enrichedInputs = rule.inputs.map((input) => {
        if (getFeatureIdFromInput(input) != null) return input;

        // Resolve featureId from current indices
        let featureId: string | undefined;
        if (input.derivedFeatureIndex !== undefined && input.derivedFeatureIndex >= 0) {
          const ref = newDerivedFeatures[input.derivedFeatureIndex];
          featureId = getDerivedFeatureId(ref);
        } else if (input.inputFeatureIndex !== undefined && input.inputFeatureIndex >= 0) {
          const ref = newInputFeatures[input.inputFeatureIndex];
          featureId = ref?.referenceId;
        }

        if (featureId) {
          anyInputChanged = true;
          return { ...input, featureId };
        }
        return input;
      });

      if (anyInputChanged) {
        anyRuleChanged = true;
        return { ...rule, inputs: enrichedInputs };
      }
      return rule;
    });

    return anyRuleChanged ? { ...feature, rules: enrichedRules } : feature;
  });
}

/**
 * Reindexes all new derived feature inputs to match their actual positions in the provided arrays.
 * Used after sorting/filtering features, as these operations invalidate array indices.
 *
 * Enriches rule inputs with featureId first to enable reliable index lookups.
 *
 * @param newDerivedFeatures - Features to reindex
 * @param newInputFeatures - Input features for index lookups
 */
export function reindexNewDerivedFeatures(
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  newInputFeatures: NewlyAddedInputFeature[],
): NewlyAddedDerivedFeature[] {
  // Enrich inputs with featureId so we can track what they reference
  const enrichedFeatures = enrichNewDerivedFeaturesWithFeatureId(newDerivedFeatures, newInputFeatures);

  const inputIndexByRefId = new Map(newInputFeatures.map((f, index) => [f.referenceId, index]));
  const derivedIndexById = new Map<string, number>();
  enrichedFeatures.forEach((f, index) => {
    const id = f.referenceId ?? f.id;
    if (id) derivedIndexById.set(id, index);
  });

  return enrichedFeatures.map((feature) => {
    if (!feature.rules?.length) return feature;

    return {
      ...feature,
      rules: feature.rules.map((rule) => ({
        ...rule,
        inputs: rule.inputs?.map((input) => {
          // If this input references an existing persisted feature, don't reindex
          if (input.inputFeatureId) {
            return input;
          }

          const featureId = getFeatureIdFromInput(input);
          if (!featureId) {
            console.warn('Input missing featureId, cannot reindex reliably:', input);
            return input;
          }

          const newInputIndex = inputIndexByRefId.get(featureId);
          const newDerivedIndex = derivedIndexById.get(featureId);

          const { inputFeatureIndex, derivedFeatureIndex, ...baseInput } = input;

          return {
            ...baseInput,
            ...(newInputIndex !== undefined && { inputFeatureIndex: newInputIndex }),
            ...(newDerivedIndex !== undefined && { derivedFeatureIndex: newDerivedIndex }),
          };
        }),
      })),
    };
  });
}

/** Creates maps for lookups of intermediate features in new and existing derived feature lists. */
const createIntermediateLookupMaps = (
  existingDerivedFeatures: TemplateDerivedFeature[],
  newDerivedFeatures: NewlyAddedDerivedFeature[],
) => {
  const existingIntermediatesById = new Map(
    existingDerivedFeatures.filter((f) => !f.isOutput && f.id).map((f) => [f.id!, f]),
  );
  const newIntermediatesByRefId = new Map(
    newDerivedFeatures
      .filter((f) => !f.isOutput && !f.intermediateAsInput && !f.mappedFeatureId)
      .map((f) => [f.referenceId!, f]),
  );
  return { existingIntermediatesById, newIntermediatesByRefId };
};

/** Recursively collects the IDs of all features that the given feature depends on. */
const collectDerivedFeatureDependencies = (
  feature: TemplateDerivedFeature | NewlyAddedDerivedFeature,
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  existingDerivedFeatures: TemplateDerivedFeature[],
): Set<string> => {
  const usedFeatureIdsForFeature = new Set<string>();

  const { existingIntermediatesById, newIntermediatesByRefId } = createIntermediateLookupMaps(
    existingDerivedFeatures,
    newDerivedFeatures,
  );

  const collectRecursive = (currentFeature: TemplateDerivedFeature | NewlyAddedDerivedFeature) => {
    currentFeature.rules?.forEach((rule) => {
      if (!Array.isArray(rule.inputs)) return;

      rule.inputs.forEach((input) => {
        const featureId = getFeatureIdFromInput(input);
        if (!featureId || usedFeatureIdsForFeature.has(featureId)) return;

        usedFeatureIdsForFeature.add(featureId);

        const intermediate = existingIntermediatesById.get(featureId) ?? newIntermediatesByRefId.get(featureId);
        if (intermediate) {
          collectRecursive(intermediate);
        }
      });
    });
  };

  collectRecursive(feature);
  return usedFeatureIdsForFeature;
};

/** Collects the IDs of all features that are used by the given output features, including their dependencies. */
const collectAllUsedFeatureIds = (
  outputs: Array<TemplateDerivedFeature | NewlyAddedDerivedFeature>,
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  existingDerivedFeatures: TemplateDerivedFeature[],
): Set<string> => {
  return new Set(
    outputs.flatMap((feature) => [
      ...collectDerivedFeatureDependencies(feature, newDerivedFeatures, existingDerivedFeatures),
    ]),
  );
};

/** After editing or deleting a derived feature, some features may no longer be used. This function collects the
 *  dependencies of all existing and new output features and removes any features that are not depended on by any
 *  output. */
export const filterUnusedFeatures = (
  existingDerivedFeatures: TemplateDerivedFeature[],
  existingInputFeatures: TemplateInput[],
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  newInputFeatures: NewlyAddedInputFeature[],
) => {
  // Enrich rule inputs with featureId so dependency tracking works correctly
  const enrichedNewDerivedFeatures = enrichNewDerivedFeaturesWithFeatureId(newDerivedFeatures, newInputFeatures);

  const existingOutputs = existingDerivedFeatures.filter((f) => f.isOutput);
  const newOutputs = enrichedNewDerivedFeatures.filter((f) => f.isOutput);

  const allOutputs = [...existingOutputs, ...newOutputs];
  const allUsedFeatureIds = collectAllUsedFeatureIds(allOutputs, enrichedNewDerivedFeatures, existingDerivedFeatures);

  // Keep existing intermediates that are mapping targets
  enrichedNewDerivedFeatures
    .filter((feature) => feature.mappedFeatureId)
    .forEach((feature) => allUsedFeatureIds.add(feature.mappedFeatureId!));

  const isUsed = (id?: string) => id && allUsedFeatureIds.has(id);

  const usedExistingDerivedFeatures = existingDerivedFeatures.filter((f) => f.isOutput !== false || isUsed(f.id));
  // Pruning existing inputs is deferred to formatTemplateInput so we don't destroy identity
  // before the user has a chance to re-reference an input via mappedFeatureId (CRAB-58728).
  const usedExistingInputFeatures = existingInputFeatures;
  const usedNewDerivedFeatures = enrichedNewDerivedFeatures.filter((f) => f.isOutput || isUsed(f.referenceId));
  const usedNewInputFeatures = newInputFeatures.filter((f) => isUsed(f.referenceId));

  return {
    usedExistingDerivedFeatures,
    usedExistingInputFeatures,
    usedNewDerivedFeatures,
    usedNewInputFeatures,
  };
};

/** True when a derived feature/input carries a `columnDefinition` reference (table-editor-sourced). */
export const isFeatureTypeDerivedFeatureWithColumnDefinition = (
  feature: unknown,
): feature is DerivedFeatureWithColumnDefinition & { columnDefinition: Pick<ColumnDefinition, 'id'> } => {
  if (!feature || typeof feature !== 'object') {
    return false;
  }
  if (!('columnDefinition' in feature)) {
    return false;
  }
  const { columnDefinition } = feature;
  return (
    !!columnDefinition &&
    typeof columnDefinition === 'object' &&
    'id' in columnDefinition &&
    typeof columnDefinition.id === 'string'
  );
};

/** Builds a camelCase Formula identifier from a Feature name, splitting on every non-alphanumeric char.
 * Non-ASCII letters and underscores are dropped, so a name that yields nothing falls back to `fallback` */
export function featureNameToIdentifier(featureName: string, fallback: string): string {
  const words = featureName.split(/[^a-zA-Z0-9]+/).filter((word) => word.length > 0);
  if (words.length === 0) return fallback;
  const identifier = words
    .map((word, index) => (index === 0 ? word.toLowerCase() : word[0].toUpperCase() + word.slice(1).toLowerCase()))
    .join('');
  return /^[0-9]/.test(identifier) ? `_${identifier}` : identifier;
}

export function makeUniqueIdentifier(base: string, usedIdentifiers: Set<string>): string {
  // The Formula language binds variables case-insensitively, so compare lowercased to avoid
  // emitting identifiers the backend would reject as duplicates (e.g. `Temp` alongside `temp`).
  const usedLowercase = new Set([...usedIdentifiers].map((name) => name.toLowerCase()));
  // Separate the counter with `_` after a digit so `valueSearch1` dedupes to `valueSearch1_2`, not `valueSearch12`.
  const separator = /[0-9]$/.test(base) ? '_' : '';
  let identifier = base;
  for (let suffix = 2; usedLowercase.has(identifier.toLowerCase()); suffix++) {
    identifier = `${base}${separator}${suffix}`;
  }
  return identifier;
}

const getInputIndex = (inputId: string, inputFeatures: TemplateInput[], inputFeatureId?: string) => {
  if (!inputFeatures?.length) return undefined;
  if (inputFeatureId) {
    return inputFeatures.findIndex(
      (feature) => feature?.id === inputFeatureId || feature?.columnDefinition?.id === inputFeatureId,
    );
  }
  return inputFeatures.findIndex(
    (feature) =>
      feature?.referenceId === inputId || feature?.id === inputId || feature?.columnDefinition?.id === inputId,
  );
};

const getDerivedIndex = (id: string, derivedFeatures: TemplateOutput[], inputFeatureId?: string) => {
  if (!derivedFeatures?.length) return undefined;
  if (inputFeatureId) {
    return derivedFeatures.findIndex(
      (feature) => feature?.id === inputFeatureId || ('featureId' in feature && feature?.featureId === inputFeatureId),
    );
  }
  return derivedFeatures.findIndex(
    (feature) =>
      feature?.id === id ||
      ('referenceId' in feature && feature?.referenceId === id) ||
      ('featureId' in feature && feature.featureId === id) ||
      (isFeatureTypeDerivedFeatureWithColumnDefinition(feature) && feature.columnDefinition?.id === id),
  );
};

/** True when an input feature already has a persisted template id that should be referenced by inputFeatureId. */
const shouldUseInputFeatureIdForRule = (feature: TemplateInput | undefined): boolean => {
  if (!feature?.id) {
    return false;
  }
  // New tool-pane inputs carry the Seeq item id in both id and referenceId; the backend assigns
  // template ids on create, so rules must reference these inputs by index instead.
  if ('referenceId' in feature && feature.referenceId && feature.id === feature.referenceId) {
    return !!(feature.canonicalId || feature.columnDefinition?.id);
  }
  return true;
};

const hasValidTemplateRuleInputReference = (input: TemplateRuleInputV1Object): boolean =>
  input.argument != null ||
  input.inputFeatureId != null ||
  input.inputFeatureIndex != null ||
  input.derivedFeatureIndex != null;

const getTemplateFeatureName = (feature: TemplateOutput | TemplateInput | undefined, fallback = ''): string => {
  if (!feature) {
    return fallback;
  }
  if ('displayName' in feature && feature.displayName) {
    return feature.displayName;
  }
  return feature.name ?? fallback;
};

const mapRuleInputToFeature = (
  inputId: string | TemplateRuleInputV1Object,
  inputFeatures: TemplateInput[],
  derivedFeatures: TemplateOutput[],
  featureType: TemplateRuleInputV1Object['type'],
): TemplateRuleInputV1Object => {
  if (typeof inputId === 'object') {
    const persistedInput = inputId;

    if (
      persistedInput.argument !== undefined &&
      persistedInput.inputFeatureId === undefined &&
      persistedInput.inputFeatureIndex === undefined &&
      persistedInput.derivedFeatureIndex === undefined &&
      !hasFeatureId(persistedInput)
    ) {
      return persistedInput;
    }
  }

  const lookupId = typeof inputId === 'object' ? (inputId.inputFeatureId ?? getFeatureIdFromInput(inputId)) : undefined;
  const inputIndexUnmapped = getInputIndex(typeof inputId === 'string' ? inputId : '', inputFeatures, lookupId);
  let finalInputIndex = inputIndexUnmapped;
  const derivedIndex = getDerivedIndex(typeof inputId === 'string' ? inputId : '', derivedFeatures, lookupId);
  let matchingInputFeature: TemplateInput | undefined;

  if (inputIndexUnmapped !== undefined && inputIndexUnmapped >= 0) {
    const feature = inputFeatures[inputIndexUnmapped];
    if (feature.mappedFeatureId) {
      const mappedIndex = inputFeatures.findIndex((f) => f.id === feature.mappedFeatureId);
      if (mappedIndex >= 0) {
        matchingInputFeature = inputFeatures[mappedIndex];
        finalInputIndex = mappedIndex;
      } else {
        matchingInputFeature = feature;
      }
    } else {
      matchingInputFeature = feature;
    }
  } else {
    matchingInputFeature = undefined;
  }

  const matchingDerivedFeature =
    derivedIndex !== undefined && derivedIndex >= 0 ? derivedFeatures[derivedIndex] : undefined;
  const feature = matchingInputFeature ?? matchingDerivedFeature;
  const featureId =
    matchingInputFeature?.referenceId ?? matchingDerivedFeature?.referenceId ?? matchingDerivedFeature?.id;
  const persistedName = typeof inputId === 'object' ? inputId.name : undefined;
  const persistedDescription = typeof inputId === 'object' ? inputId.description : undefined;

  if (finalInputIndex !== undefined && finalInputIndex >= 0) {
    if (shouldUseInputFeatureIdForRule(matchingInputFeature)) {
      return {
        name: getTemplateFeatureName(feature, persistedName ?? ''),
        description: feature?.description ?? persistedDescription ?? '',
        type: featureType,
        inputFeatureId: matchingInputFeature!.id,
      };
    }
    return {
      name: getTemplateFeatureName(feature, persistedName ?? ''),
      description: feature?.description ?? persistedDescription ?? '',
      type: featureType,
      inputFeatureIndex: finalInputIndex,
    };
  }

  if (derivedIndex !== undefined && derivedIndex >= 0) {
    return {
      name: getTemplateFeatureName(feature, persistedName ?? ''),
      description: feature?.description ?? persistedDescription ?? '',
      type: featureType,
      derivedFeatureIndex: derivedIndex,
    };
  }

  if (matchingInputFeature?.id && !matchingInputFeature.referenceId) {
    return {
      name: matchingInputFeature.name ?? persistedName ?? '',
      description: matchingInputFeature.description ?? persistedDescription ?? '',
      type: featureType,
      inputFeatureId: matchingInputFeature.id,
    };
  }

  if (lookupId || persistedName) {
    const matchingInput = inputFeatures.find(
      (input) =>
        (lookupId &&
          (input.id === lookupId || input.referenceId === lookupId || input.columnDefinition?.id === lookupId)) ||
        (persistedName && input.name === persistedName),
    );
    if (matchingInput) {
      if (shouldUseInputFeatureIdForRule(matchingInput)) {
        return {
          name: matchingInput.name ?? persistedName ?? '',
          description: matchingInput.description ?? persistedDescription ?? '',
          type: featureType,
          inputFeatureId: matchingInput.id,
        };
      }
      const inputIndex = inputFeatures.indexOf(matchingInput);
      if (inputIndex >= 0) {
        return {
          name: matchingInput.name ?? persistedName ?? '',
          description: matchingInput.description ?? persistedDescription ?? '',
          type: featureType,
          inputFeatureIndex: inputIndex,
        };
      }
    }

    if (typeof inputId === 'object' && inputId.inputFeatureId && inputId.inputFeatureId === lookupId) {
      return {
        name: persistedName ?? '',
        description: persistedDescription ?? '',
        type: featureType,
        inputFeatureId: lookupId,
      };
    }
  }

  const fallbackInput: TemplateRuleInputV1Object = {
    name: getTemplateFeatureName(feature, persistedName ?? ''),
    description: feature?.description ?? persistedDescription ?? '',
    type: featureType,
    ...(shouldUseInputFeatureIdForRule(matchingInputFeature) && featureId && { inputFeatureId: featureId }),
  };

  if (!hasValidTemplateRuleInputReference(fallbackInput)) {
    throw new Error(`Unable to resolve template rule input reference for save: ${JSON.stringify(inputId)}`);
  }

  return fallbackInput;
};

/** Maps rule inputs to the expected feature format for the backend. If an input feature has a mappedFeatureId, it
 *  uses that feature instead.
 *
 * @param ruleInputs - The rule inputs to map
 * @param inputFeatures - The current list of input features in the template
 * @param derivedFeatures - The current list of derived features in the template
 * @param featureType - The feature type to use for the mapped features
 * @returns An array of mapped features
 */
export function mapRuleInputsToFeatures(
  ruleInputs: Array<string | TemplateRuleInputV1Object>,
  inputFeatures: TemplateInput[],
  derivedFeatures: TemplateOutput[],
  featureType: TemplateRuleInputV1Object['type'],
) {
  return (ruleInputs ?? []).map((inputId) =>
    mapRuleInputToFeature(inputId, inputFeatures, derivedFeatures, featureType),
  );
}

/**
 * A template never authors the scope of the items it creates; appserver fills it in when the template is
 * applied. Anything scope-related that comes back on read is therefore appserver's, and sending it back would
 * only re-pin the template to the workbook it was authored in.
 */
export const isScopedToRuleInput = (input: string | { name?: string }): boolean =>
  typeof input !== 'string' && input.name === SeeqNames.MaterializedTables.Parameters.ScopedTo;

/** Drops the static `scopedTo` argument from a creator rule. */
export const omitScopedTo = <T extends { scopedTo?: unknown }>(creatorRule: T): Omit<T, 'scopedTo'> => {
  const { scopedTo, ...withoutScopedTo } = creatorRule;
  return withoutScopedTo;
};

/** Converts a formula into the format expected by the backend for a formula creator rule */
export const toFormulaCreatorRule = (formula: FormulaOutput, featureName: string): TemplateFormulaCreatorRuleV1 => {
  const parameterNames = (formula.parameters ?? [])
    .map((parameter) => parameter.name)
    .filter((name): name is string => !!name);
  const makeStringInput = (name: string, argument: string): TemplateRuleInputV1String => ({
    name,
    type: TemplateRuleInputV1StringTypeEnum.TEXT,
    argument,
  });
  const makeListStringInput = (name: string, argument: string[]): TemplateRuleInputV1ListString => ({
    name,
    type: TemplateRuleInputV1ListStringTypeEnum.TEXT,
    argument,
  });
  return {
    name: makeStringInput('name', featureName),
    formula: makeStringInput('formula', formula.formula ?? ''),
    parameters: makeListStringInput('parameters', parameterNames),
    variableParameters: makeListStringInput('variableParameters', []),
    variableParameterStrings: makeListStringInput('variableParameterStrings', []),
  };
};

/** Rewrites each parameter's name (and the corresponding `$identifier` tokens in the formula string)
 * to a lowerCamelCase identifier derived from the Feature's current display name. */
export function deriveNameBasedParameterIdentifiers(
  formula: FormulaOutput,
  resolvedFeatureNames: string[],
): FormulaOutput {
  const parameters = formula.parameters ?? [];
  const usedIdentifiers = new Set<string>();
  const identifierByOldName = new Map<string, string>();

  const newParameters = parameters.map((param, i) => {
    const oldId = param.name ?? '';
    // Fall back to the backend's alphabetic identifier when the Feature can't be resolved.
    const candidate = featureNameToIdentifier(resolvedFeatureNames[i] ?? '', oldId);
    const finalId = makeUniqueIdentifier(candidate, usedIdentifiers);
    usedIdentifiers.add(finalId);
    identifierByOldName.set(oldId, finalId);

    return { ...param, name: finalId };
  });

  return {
    ...formula,
    formula: renameFormulaVariables(formula.formula ?? '', identifierByOldName),
    parameters: newParameters,
  };
}

/**
 * Reindexes rule inputs to match an input array, handling mapped inputs.
 * When an input is mapped, uses the mapped feature's index instead.
 */
function reindexRuleInputsForMappedFeatures(
  rules: FeatureRuleV1[] | undefined,
  inputFeatures: (TemplateInput | NewlyAddedInputFeature)[],
  derivedForLookup?: NewlyAddedDerivedFeature[],
): FeatureRuleV1[] | undefined {
  if (!rules) return rules;

  return rules.map((rule) => ({
    ...rule,
    inputs: rule.inputs?.map((input) => {
      if (typeof input !== 'object' || !hasFeatureId(input)) return input;

      let inputIndex = inputFeatures.findIndex((f) => (f.referenceId ?? f.id) === input.featureId);

      // If the input has mappedFeatureId, use the mapped feature's index instead
      if (inputIndex >= 0) {
        const foundInput = inputFeatures[inputIndex];
        if (foundInput.mappedFeatureId) {
          const mappedIndex = inputFeatures.findIndex((f) => f.id === foundInput.mappedFeatureId);
          if (mappedIndex >= 0) {
            inputIndex = mappedIndex;
          }
        }
      }

      const derivedIndex = derivedForLookup
        ? derivedForLookup.findIndex((f) => (f.referenceId ?? f.id) === input.featureId)
        : -1;

      return {
        ...input,
        ...(inputIndex >= 0 && { inputFeatureIndex: inputIndex }),
        ...(derivedIndex >= 0 && { derivedFeatureIndex: derivedIndex }),
      };
    }),
  }));
}

/** Builds backend-formatted rules for each derived feature by combining its saved formula with
 * the template's full input list. Features without a matching formula in the map are returned as-is. */
export function formatDerivedFeaturesUsingFormulas(
  derivedFeatures: NewlyAddedDerivedFeature[],
  inputFeatures: NewlyAddedInputFeature[],
  formulasByFeatureId: Map<string, FormulaOutput>,
  allDerivedForIndexLookup?: NewlyAddedDerivedFeature[],
): NewlyAddedDerivedFeature[] {
  // Use allDerivedForIndexLookup if provided, otherwise use derivedFeatures
  const derivedForLookup = allDerivedForIndexLookup ?? derivedFeatures;

  return derivedFeatures.map((feature) => {
    const featureId = getDerivedFeatureId(feature);
    if (!featureId) return feature;

    const formula = formulasByFeatureId.get(featureId);
    if (formula) {
      const ruleInputs = mapRuleInputsToFeatures(
        (formula.parameters ?? []).map((parameter) => parameter.item?.id) as string[],
        inputFeatures,
        derivedForLookup as TemplateOutput[],
        TemplateRuleInputV1ObjectTypeEnum.FEATUREID,
      );
      const resolvedFeatureNames = ruleInputs.map((input) => input.name ?? '');
      const formulaWithDerivedIdentifiers = deriveNameBasedParameterIdentifiers(formula, resolvedFeatureNames);
      const rules: FeatureRuleV1[] = [
        {
          inputs: ruleInputs as TemplateRuleInputV1Object[],
          formulaCreator: toFormulaCreatorRule(formulaWithDerivedIdentifiers, feature.name),
        },
      ];
      return { ...feature, rules };
    }

    if (!feature.rules?.length) return feature;

    const reindexedRules = reindexRuleInputsForMappedFeatures(feature.rules, inputFeatures, derivedForLookup);

    return { ...feature, rules: reindexedRules };
  });
}

/** Existing InputFeature ids still referenced by the draft — directly by an existing or new
 *  derived feature's rule, or indirectly via a new input's mappedFeatureId alias. */
export const getReferencedExistingInputIds = (
  existingDerivedFeatures: TemplateDerivedFeature[],
  newInputFeatures: NewlyAddedInputFeature[],
  newDerivedFeatures: NewlyAddedDerivedFeature[],
): Set<string> => {
  const ids = new Set<string>();
  // New derived features reference existing features by inputFeatureId. Intermediates toggled to inputs aren't saved.
  const savedNewDerivedFeatures = newDerivedFeatures.filter((df) => !df.intermediateAsInput);
  [...existingDerivedFeatures, ...savedNewDerivedFeatures].forEach((df) =>
    df.rules?.forEach((rule) =>
      rule.inputs?.forEach((input) => {
        if (input.inputFeatureId) ids.add(input.inputFeatureId);
      }),
    ),
  );
  newInputFeatures.forEach((f) => {
    if (f.mappedFeatureId) ids.add(f.mappedFeatureId);
  });
  return ids;
};

export const toNewlyAddedInputFeature = (feature: TemplateInput): NewlyAddedInputFeature => {
  const id = feature.id ?? feature.referenceId ?? '';
  return {
    ...feature,
    id,
    referenceId: id,
  };
};

/**
 * Reindexes existing derived features to match a reordered input array.
 * Handles mapped inputs by updating indices to point to the mapped feature.
 */
export function reindexExistingDerivedFeatures(
  existingDerivedFeatures: TemplateDerivedFeature[],
  sortedInputFeatures: TemplateInput[],
): TemplateDerivedFeature[] {
  return existingDerivedFeatures.map((feature) => ({
    ...feature,
    rules: reindexRuleInputsForMappedFeatures(feature.rules, sortedInputFeatures) ?? feature.rules,
  }));
}

/**
 * Identifies mapped intermediates in the derived features array and returns their indices
 * and a mapping from each index to the existing feature it maps to.
 */
function getMappedIntermediateInfo(
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  existingDerivedFeatures: TemplateDerivedFeature[],
): {
  mappedIndices: Set<number>;
  mappedIndexToExistingFeature: Map<number, TemplateDerivedFeature>;
} {
  const mappedIndices = new Set<number>();
  const mappedIndexToExistingFeature = new Map<number, TemplateDerivedFeature>();
  const existingById = new Map(existingDerivedFeatures.filter((f) => f.id).map((f) => [f.id!, f]));

  newDerivedFeatures.forEach((feature, index) => {
    if (feature.mappedFeatureId && !feature.isOutput) {
      const existingFeature = existingById.get(feature.mappedFeatureId);
      if (existingFeature) {
        mappedIndices.add(index);
        mappedIndexToExistingFeature.set(index, existingFeature);
      }
    }
  });

  return { mappedIndices, mappedIndexToExistingFeature };
}

/**
 * Converts derivedFeatureIndex references that point to mapped intermediates into
 * inputFeatureId references, and removes the derivedFeatureIndex from those inputs.
 * Non-mapped references are left unchanged (reindexNewDerivedFeatures handles them).
 */
function convertMappedFeatureIndexToInputFeatureId(
  features: NewlyAddedDerivedFeature[],
  mappedIndices: Set<number>,
  mappedIndexToExistingFeature: Map<number, TemplateDerivedFeature>,
): NewlyAddedDerivedFeature[] {
  return features.map((feature) => ({
    ...feature,
    rules: feature.rules?.map((rule) => ({
      ...rule,
      inputs: rule.inputs?.map((input) => {
        if (
          typeof input === 'object' &&
          input.derivedFeatureIndex !== undefined &&
          mappedIndices.has(input.derivedFeatureIndex)
        ) {
          const existingFeature = mappedIndexToExistingFeature.get(input.derivedFeatureIndex);
          if (existingFeature?.id) {
            const { derivedFeatureIndex, ...baseInput } = input;
            return { ...baseInput, inputFeatureId: existingFeature.id, name: existingFeature.name };
          }
        }
        return input;
      }),
    })),
  }));
}

/**
 * Resolves mapped intermediates by removing them from the array and rewriting
 * rule inputs that reference them. References to mapped intermediates are converted
 * from derivedFeatureIndex (array position) to inputFeatureId (existing intermediate ID).
 * Returns both the filtered derived features and the cleaned inputs.
 *
 * @param newDerivedFeatures - The array of newly added derived features, which may contain mapped intermediates.
 * @param existingDerivedFeatures - The array of existing derived features, used for dependency lookups when filtering unused features.
 * @param newInputFeatures - The array of newly added input features, used for dependency lookups when filtering unused features and for reindexing.
 * @param inputFeaturesForReindex - Optional combined input features array used for reindexing.
 *   When omitted, uses newInputFeatures. Pass the full array (new + existing) when derived
 *   feature rules reference existing inputs.
 */
export function resolveMappedIntermediates(
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  existingDerivedFeatures: TemplateDerivedFeature[],
  newInputFeatures: NewlyAddedInputFeature[],
  inputFeaturesForReindex?: NewlyAddedInputFeature[],
): { derivedFeatures: NewlyAddedDerivedFeature[]; inputFeatures: NewlyAddedInputFeature[] } {
  const { mappedIndices, mappedIndexToExistingFeature } = getMappedIntermediateInfo(
    newDerivedFeatures,
    existingDerivedFeatures,
  );

  if (mappedIndices.size === 0) {
    return { derivedFeatures: newDerivedFeatures, inputFeatures: newInputFeatures };
  }

  const convertedIndexes = convertMappedFeatureIndexToInputFeatureId(
    newDerivedFeatures,
    mappedIndices,
    mappedIndexToExistingFeature,
  );
  const filtered = convertedIndexes.filter((_, index) => !mappedIndices.has(index));
  const reindexed = reindexNewDerivedFeatures(filtered, inputFeaturesForReindex ?? newInputFeatures);

  const { usedNewDerivedFeatures, usedNewInputFeatures } = filterUnusedFeatures(
    existingDerivedFeatures,
    [],
    reindexed,
    newInputFeatures,
  );

  return { derivedFeatures: usedNewDerivedFeatures, inputFeatures: usedNewInputFeatures };
}

const toFormattedInputFeature = (feature: NewlyAddedInputFeature | TemplateInput) => ({
  name: feature.name,
  description: feature.description,
  outputType: OutputTypeEnum.ITEMID,
  id: 'referenceId' in feature ? '' : feature.id,
  canonicalId: 'referenceId' in feature ? undefined : feature.canonicalId,
  // Only include defined restrictions; omitting them clears the restriction.
  ...(feature.outputItemType !== undefined && { outputItemType: feature.outputItemType }),
  ...(feature.outputItemMetadata !== undefined && { outputItemMetadata: feature.outputItemMetadata }),
});

/** Strips featureId from rule inputs before sending to the backend */
const stripFeatureIdFromInputs = (rules: FeatureRuleV1[] | undefined): FeatureRuleV1[] => {
  if (!rules) return [];
  return rules.map((rule) => ({
    ...rule,
    inputs: Array.isArray(rule.inputs)
      ? rule.inputs.map((input) => {
          if (typeof input === 'object' && 'featureId' in input) {
            const { featureId, ...rest } = input as TemplateRuleInputWithFeatureId;
            return rest;
          }
          return input;
        })
      : rule.inputs,
  }));
};

const toFormattedDerivedFeature = (feature: NewlyAddedDerivedFeature): DerivedFeatureV1 => ({
  id: '',
  name: feature.name,
  rules: stripFeatureIdFromInputs(feature.rules),
  outputType: OutputTypeEnum.ITEMID,
  isOutput: feature.isOutput,
  outputItemType: feature.outputItemType,
  outputItemMetadata: feature.outputItemMetadata,
});

/** Strips UI-only fields from TemplateDerivedFeature to get a clean DerivedFeatureV1 for the backend */
const stripUIFields = (feature: TemplateDerivedFeature): DerivedFeatureV1 => ({
  id: feature.id,
  canonicalId: feature.canonicalId,
  name: feature.name,
  rules: stripFeatureIdFromInputs(feature.rules),
  outputType: feature.outputType,
  isOutput: feature.isOutput,
  outputItemType: feature.outputItemType,
  outputItemMetadata: feature.outputItemMetadata,
});

/** Removes every trace of a rule's scope from a template payload, so appserver can re-add its own. */
const stripScopeFromRules = (features: DerivedFeatureV1[]): DerivedFeatureV1[] =>
  features.map((feature) => ({
    ...feature,
    rules: (feature.rules ?? []).map((rule) => ({
      ...rule,
      ...(rule.formulaCreator && { formulaCreator: omitScopedTo(rule.formulaCreator) }),
      ...(rule.stringScalarCreator && { stringScalarCreator: omitScopedTo(rule.stringScalarCreator) }),
      ...(rule.numericScalarCreator && { numericScalarCreator: omitScopedTo(rule.numericScalarCreator) }),
      inputs: (rule.inputs ?? []).filter((input) => !isScopedToRuleInput(input)),
    })),
  }));

const buildFormattedTemplate = ({
  name,
  description,
  templateId,
  scopedTo,
  inputFeatures,
  instanceNameFeature,
  scopedToFeature,
  newDerivedFeatures,
  existingDerivedFeatures,
}: {
  name: string;
  description: string;
  templateId: string;
  scopedTo?: string;
  inputFeatures: TemplateInput[];
  instanceNameFeature?: TemplateInput;
  scopedToFeature?: TemplateInput;
  newDerivedFeatures: DerivedFeatureV1[];
  existingDerivedFeatures: TemplateDerivedFeature[];
}) => {
  const formattedInputFeatures = inputFeatures.map((feature) => toFormattedInputFeature(feature));
  // Backend-managed features go back verbatim and last: toFormattedInputFeature would rewrite their
  // outputType, and the backend keys a template's recorded values on the identity they already carry.
  const inputsIncludingSystemFeatures = [
    ...formattedInputFeatures,
    ...(instanceNameFeature ? [instanceNameFeature] : []),
    ...(scopedToFeature ? [scopedToFeature] : []),
  ];

  return {
    name,
    description,
    id: templateId,
    scopedTo,
    derivedFeatures: stripScopeFromRules([...newDerivedFeatures, ...existingDerivedFeatures.map(stripUIFields)]),
    inputFeatures: inputsIncludingSystemFeatures,
  };
};

/** Builds the API input shape for editing a template from the EditTemplateTool's working state.
 *
 * When the template contains mapped inputs, derived feature rules are recalculated using each
 * feature's saved formula. The formulas are passed in via `formulasByFeatureId` (see
 * {@link fetchFormulasForFeatures} for fetching them). When no mapped inputs exist, the formula
 * map isn't consulted — pass an empty map.
 *
 * Callers are responsible for fetching the formula map and persisting the returned input. */
export const formatTemplateInput = ({
  name,
  description,
  templateId,
  scopedTo,
  newInputFeatures,
  existingInputFeatures,
  instanceNameFeature,
  scopedToFeature,
  newDerivedFeatures,
  existingDerivedFeatures,
  formulasByFeatureId,
}: {
  name: string;
  description: string;
  templateId: string;
  scopedTo?: string;
  newInputFeatures: NewlyAddedInputFeature[];
  existingInputFeatures: TemplateInput[];
  instanceNameFeature?: TemplateInput;
  scopedToFeature?: TemplateInput;
  newDerivedFeatures: NewlyAddedDerivedFeature[];
  existingDerivedFeatures: TemplateDerivedFeature[];
  formulasByFeatureId: Map<string, FormulaOutput>;
}): TemplateInputV1 => {
  // Save-time cleanup for the edit-time deferral in filterUnusedFeatures: drop any existing
  // input no rule or alias references, otherwise the server rejects the template as invalid.
  const referencedExistingInputIds = getReferencedExistingInputIds(
    existingDerivedFeatures,
    newInputFeatures,
    newDerivedFeatures,
  );
  const referencedExistingInputFeatures = existingInputFeatures.filter(
    (f) => f.id !== undefined && referencedExistingInputIds.has(f.id),
  );

  const hasMappedInputs = [...newInputFeatures, ...referencedExistingInputFeatures].some(
    (feature) => feature.mappedFeatureId,
  );

  // Filter out intermediates toggled to inputs, resolve mapped intermediates, and clean up orphans
  const derivedFeaturesToSave = newDerivedFeatures.filter((f) => !f.intermediateAsInput);
  const combinedInputsForReindex = [
    ...newInputFeatures,
    ...referencedExistingInputFeatures.map(toNewlyAddedInputFeature),
  ];
  // Reindex after filtering to fix derivedFeatureIndex references
  const reindexedAfterFiltering = reindexNewDerivedFeatures(derivedFeaturesToSave, combinedInputsForReindex);
  const { derivedFeatures: resolvedDerivedFeatures, inputFeatures: cleanedNewInputs } = resolveMappedIntermediates(
    reindexedAfterFiltering,
    existingDerivedFeatures,
    newInputFeatures,
    combinedInputsForReindex,
  );

  if (!hasMappedInputs) {
    return buildFormattedTemplate({
      name,
      description,
      templateId,
      scopedTo,
      inputFeatures: [...cleanedNewInputs, ...referencedExistingInputFeatures],
      instanceNameFeature,
      scopedToFeature,
      newDerivedFeatures: resolvedDerivedFeatures.map(toFormattedDerivedFeature),
      existingDerivedFeatures,
    });
  }

  const sortedNewInputs = [
    ...cleanedNewInputs.filter((feature) => !feature.mappedFeatureId),
    ...cleanedNewInputs.filter((feature) => feature.mappedFeatureId),
  ];
  const sortedNewAndExistingInputFeatures = [...referencedExistingInputFeatures, ...sortedNewInputs];

  const derivedFeaturesWithRebuiltFormulas = formatDerivedFeaturesUsingFormulas(
    resolvedDerivedFeatures,
    sortedNewAndExistingInputFeatures.map(toNewlyAddedInputFeature),
    formulasByFeatureId,
  );

  const formattedNewDerivedFeatures = resolveMappedIntermediates(
    derivedFeaturesWithRebuiltFormulas,
    existingDerivedFeatures,
    cleanedNewInputs,
  ).derivedFeatures.map(toFormattedDerivedFeature);

  const allInUseInputFeatures = [
    ...referencedExistingInputFeatures,
    ...cleanedNewInputs.filter((feature) => !feature.mappedFeatureId),
  ];

  const reindexedExistingDerivedFeatures = reindexExistingDerivedFeatures(
    existingDerivedFeatures,
    allInUseInputFeatures,
  );

  return buildFormattedTemplate({
    name,
    description,
    templateId,
    scopedTo,
    inputFeatures: allInUseInputFeatures,
    instanceNameFeature,
    scopedToFeature,
    newDerivedFeatures: formattedNewDerivedFeatures,
    existingDerivedFeatures: reindexedExistingDerivedFeatures,
  });
};

/**
 * Returns a copy of the Derived Feature with `newName` applied to its top-level name AND to each
 * Column Rule's `name` argument across the possible rules creators
 */
export function updateFeatureName<T extends { name?: string; rules?: FeatureRuleV1[] }>(
  feature: T,
  newName: string,
): T {
  const renameRule = <R extends { name?: TemplateRuleInputV1String }>(rule: R | undefined): R | undefined =>
    rule?.name?.type === TemplateRuleInputV1StringTypeEnum.TEXT
      ? { ...rule, name: { ...rule.name, argument: newName } }
      : rule;
  return {
    ...feature,
    name: newName,
    rules: feature.rules?.map((rule) => ({
      ...rule,
      formulaCreator: renameRule(rule.formulaCreator),
      numericScalarCreator: renameRule(rule.numericScalarCreator),
      stringScalarCreator: renameRule(rule.stringScalarCreator),
    })),
  };
}
